
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckSquare } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface ProfileFormValues {
  displayName: string;
  tosAccepted: boolean;
}

const SetupProfile = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [validationError, setValidationError] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const form = useForm<ProfileFormValues>({
    defaultValues: {
      displayName: "",
      tosAccepted: false
    },
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setInitialLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Auth session check:", session ? "Session exists" : "No session");
        
        if (!session) {
          console.log("No session, redirecting to auth");
          navigate('/auth');
          return;
        }
        
        setUser(session.user);
        
        // Check if profile is already set up
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('display_name, tos_accepted')
          .eq('id', session.user.id)
          .maybeSingle();
          
        if (error) {
          console.error("Error checking profile:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Could not check if profile exists",
          });
          return;
        }
        
        console.log("Profile check result:", profile);
            
        // Only redirect if display_name exists and is not empty and TOS is accepted
        if (profile?.display_name && profile.display_name.trim() !== '' && profile.tos_accepted === true) {
          console.log("Profile already set up with name:", profile.display_name);
          console.log("Redirecting to home from profile check");
          
          // Wait a bit before redirecting
          setTimeout(() => {
            navigate('/');
          }, 1000);
          return;
        }
        
        // Pre-fill with existing values if available
        if (profile) {
          if (profile.display_name) {
            form.setValue('displayName', profile.display_name);
          }
          if (profile.tos_accepted) {
            form.setValue('tosAccepted', true);
          }
        }
        
        // If no pre-filled values, use fallbacks
        if (!form.getValues('displayName')) {
          // Check user metadata first
          const metadataDisplayName = session.user.user_metadata?.display_name;
          
          if (metadataDisplayName) {
            console.log("Found display name in metadata:", metadataDisplayName);
            form.setValue('displayName', metadataDisplayName);
          } else {
            // Default to email prefix as suggestion only
            const emailPrefix = session.user.email?.split('@')[0] || '';
            console.log("Using email prefix as suggestion:", emailPrefix);
            form.setValue('displayName', emailPrefix);
          }
        }
      } catch (error: any) {
        console.error("Error in checkAuth:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "An error occurred",
        });
      } finally {
        setInitialLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate, form, toast]);

  const validateDisplayName = (name: string): boolean => {
    if (!name || name.trim().length < 3) {
      setValidationError("Display name must be at least 3 characters long");
      return false;
    }
    setValidationError("");
    return true;
  };

  const onSubmit = async (data: ProfileFormValues) => {
    if (!user) {
      console.error("Cannot update profile: User is not defined");
      return;
    }
    
    // Validate the display name length
    if (!validateDisplayName(data.displayName)) {
      return;
    }
    
    // Validate TOS acceptance
    if (!data.tosAccepted) {
      setValidationError("You must accept the Terms of Service to continue");
      return;
    }
    
    try {
      setLoading(true);
      console.log("Submitting profile update with:", {
        displayName: data.displayName,
        tosAccepted: data.tosAccepted
      });
      
      const { data: result, error } = await supabase
        .from('profiles')
        .update({
          display_name: data.displayName.trim(),
          tos_accepted: data.tosAccepted
        })
        .eq('id', user.id);

      if (error) {
        console.error("Error updating profile:", error);
        throw error;
      }

      console.log("Profile updated successfully:", result);
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully set up.",
      });

      // Add a longer delay before redirecting to ensure the profile is saved
      console.log("Waiting before redirecting to home...");
      setTimeout(() => {
        console.log("Now redirecting to home after profile update");
        navigate('/');
      }, 1500);
    } catch (error: any) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred while updating your profile",
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Loading your profile...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Complete Your Profile</CardTitle>
          <CardDescription>
            Let's get to know you better. Choose how you want to be known in the community.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {validationError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name <span className="text-xs text-gray-500">(min. 3 characters)</span></FormLabel>
                    <FormControl>
                      <Input 
                        {...field}
                        placeholder="Enter your display name"
                        disabled={loading}
                        minLength={3}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value.trim().length >= 3) {
                            setValidationError("");
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tosAccepted"
                    checked={form.watch('tosAccepted')}
                    onCheckedChange={(checked) => {
                      form.setValue('tosAccepted', checked as boolean);
                      if (checked) setValidationError("");
                    }}
                  />
                  <Label 
                    htmlFor="tosAccepted" 
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    I accept the <a href="/terms-of-service" target="_blank" className="text-blue-600 hover:underline">Terms of Service</a>
                  </Label>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? "Saving..." : "Complete Profile"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupProfile;
